import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  PhoneArrowUpRightIcon,
  PhoneXMarkIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { agentService, AgentCampaign } from '../../services/agentService';
import { leadService } from '../../services/leadService';
import { useWebRTC } from '../../contexts/WebRTCContext';
import type { OutboundCallParams } from '../../contexts/WebRTCContext';
import type { LeadStatus } from '../../types/lead';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import toast from 'react-hot-toast';
import { buildTimezoneFilterMessage, type ManualDialSelectionMeta } from './timezoneFilterMessaging';

const PAGE_SIZE = 25;

const NON_DIALABLE_STATUSES = ['calling'];
const FIRST_DIAL_STATUSES = ['new'];

type StatusFilter = 'all' | LeadStatus;

function dispositionToLeadStatus(disposition: string): string {
  return disposition === 'voicemail' ? 'no_answer' : disposition;
}
type StatusFilterOption = { value: StatusFilter; label: string };

const STATUS_FILTER_OPTIONS: StatusFilterOption[] = [
  { value: 'new', label: 'New' },
  { value: 'called', label: 'Called' },
  { value: 'interested', label: 'Interested' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'busy', label: 'Busy' },
  { value: 'unreachable', label: 'Unreachable' },
  { value: 'failed', label: 'Failed' },
  { value: 'do_not_call', label: 'Do Not Call' },
  { value: 'out_of_business', label: 'Out of Business' },
  { value: 'wrong_number', label: 'Wrong Number' },
  { value: 'unqualified', label: 'Unqualified' },
  { value: 'app_in', label: 'App In' },
  { value: 'app_out', label: 'App Out' },
  { value: 'gatekeeper', label: 'Gatekeeper' },
  { value: 'operator', label: 'Operator' },
  { value: 'deceased', label: 'Deceased' },
  { value: 'spanish', label: 'Spanish' },
  { value: 'calling', label: 'Calling' },
];

interface Lead {
  id: string;
  full_name: string | null;
  phone_e164: string | null;
  email: string | null;
  company_name: string | null;
  status: string;
  campaign_name: string | null;
  latest_call_status: string | null;
  latest_reason: string | null;
  notes: string | null;
  dial_count: number;
  created_at: string;
  phone_number_count?: number;
  active_phone_number_count?: number;
  untried_phone_number_count?: number;
  area_code?: string | null;
  timezone?: 'Eastern' | 'Central' | 'Mountain' | 'Pacific' | 'Unknown' | null;
  pickup_rate?: number;
  priority_tier?: 'High' | 'Medium' | 'Low' | null;
}

const AgentManualDialPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = location.state as {
    returnedFromCall?: boolean;
    campaignId?: string;
  } | null;

  // Campaign selection
  const [campaigns, setCampaigns] = useState<AgentCampaign[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [campaignsLoading, setCampaignsLoading] = useState(true);

  // Leads
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [selectionMeta, setSelectionMeta] = useState<ManualDialSelectionMeta | null>(null);
  const { status: webrtcStatus, makeOutboundCall, hangUp, setOnDispositionComplete } = useWebRTC();
  const [clearingStuck, setClearingStuck] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [timezoneFilter, setTimezoneFilter] = useState<string>('auto');
  const [callAttemptsFilter, setCallAttemptsFilter] = useState<string>('0');
  const [searchQuery, setSearchQuery] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Dial state
  const [myDialingLeadIds, setMyDialingLeadIds] = useState<Set<string>>(new Set());
  const [otherAgentLeads, setOtherAgentLeads] = useState<Map<string, string>>(new Map()); // leadId -> agentName
  const [cancellingLeadId, setCancellingLeadId] = useState<string | null>(null);
  const [editingNotesLeadId, setEditingNotesLeadId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotesLeadId, setSavingNotesLeadId] = useState<string | null>(null);

  // Get manual campaigns only
  const manualCampaigns = campaigns.filter(
    (c) => c.dialing_type === 'manual' && c.campaign_type === 'human' && c.status === 'active'
  );

  // Fetch campaigns on mount
  useEffect(() => {
    setCampaignsLoading(true);
    agentService
      .getMyCampaigns()
      .then((data) => {
        setCampaigns(data);
        // If returning from a call, select the campaign we were dialing
        const returnCampaignId = returnState?.campaignId;
        const manual = data.filter(
          (c) => c.dialing_type === 'manual' && c.campaign_type === 'human' && c.status === 'active'
        );
        if (returnCampaignId && manual.some(c => c.id === returnCampaignId)) {
          setSelectedCampaignId(returnCampaignId);
        } else if (manual.length > 0 && !selectedCampaignId) {
          setSelectedCampaignId(manual[0].id);
        }
      })
      .catch(() => toast.error('Failed to load campaigns'))
      .finally(() => setCampaignsLoading(false));
  }, []);

  // Handle return from agent dashboard after a call
  useEffect(() => {
    if (returnState?.returnedFromCall) {
      toast.success('Call completed.', { icon: '✅', duration: 2000 });
    }
  }, [returnState?.returnedFromCall]);

  // SSE subscription for lead dial status (with fallback polling on disconnect)
  useEffect(() => {
    if (!selectedCampaignId) return;

    let es: EventSource | null = null;
    let fallbackPoll: ReturnType<typeof setInterval> | null = null;
    let stopped = false;

    const applyDialStatus = (data: any) => {
      const selfId = data.selfId;
      const dialingLeads: Array<{ leadId: string; agentId: string; agentName: string }> = data.dialingLeads || [];

      const myIds = new Set<string>();
      const othersMap = new Map<string, string>();

      for (const dl of dialingLeads) {
        if (dl.agentId === selfId) {
          myIds.add(dl.leadId);
        } else {
          othersMap.set(dl.leadId, dl.agentName);
        }
      }

      setMyDialingLeadIds(myIds);
      setOtherAgentLeads(othersMap);
    };

    const startFallback = () => {
      if (fallbackPoll || stopped) return;
      fallbackPoll = setInterval(async () => {
        try {
          const data = await agentService.getLeadDialStatus(selectedCampaignId);
          applyDialStatus(data);
        } catch {}
      }, 5000);
    };

    const stopFallback = () => {
      if (fallbackPoll) {
        clearInterval(fallbackPoll);
        fallbackPoll = null;
      }
    };

    try {
      const url = agentService.getLeadDialStatusStreamUrl(selectedCampaignId);
      es = new EventSource(url);

      es.addEventListener('lead-dial-status', (event) => {
        try {
          applyDialStatus(JSON.parse(event.data));
        } catch {}
      });

      es.onerror = () => {
        es?.close();
        es = null;
        startFallback();
      };
    } catch {
      startFallback();
    }

    return () => {
      stopped = true;
      if (es) es.close();
      stopFallback();
    };
  }, [selectedCampaignId]);

  // When WebRTC recovers to 'ready' while we still have local dialing leads,
  // sync with server to clear any stale state (e.g. call failed silently).
  useEffect(() => {
    if (webrtcStatus !== 'ready' || myDialingLeadIds.size === 0 || !selectedCampaignId) return;
    const timeout = setTimeout(async () => {
      try {
        const data = await agentService.getLeadDialStatus(selectedCampaignId);
        const selfId = data.selfId;
        const dialingLeads: Array<{ leadId: string; agentId: string }> = data.dialingLeads || [];
        const serverMyIds = new Set(dialingLeads.filter((dl) => dl.agentId === selfId).map((dl) => dl.leadId));
        setMyDialingLeadIds((prev) => {
          const stillDialing = new Set<string>();
          for (const id of prev) {
            if (serverMyIds.has(id)) stillDialing.add(id);
          }
          if (stillDialing.size === prev.size) return prev;
          return stillDialing;
        });
      } catch {}
    }, 3000);
    return () => clearTimeout(timeout);
  }, [webrtcStatus, selectedCampaignId]);

  // Fetch leads when campaign/filter/page changes
  const fetchLeads = useCallback(
    async (page = 1) => {
      if (!selectedCampaignId) return;

      setLeadsLoading(true);
      try {
        const data = await agentService.getMyResults({
          campaignId: selectedCampaignId,
          status: statusFilter === 'all' ? undefined : statusFilter,
          search: searchQuery || undefined,
          timezone: timezoneFilter !== 'auto' ? timezoneFilter : undefined,
          callAttempts: callAttemptsFilter !== 'all' ? callAttemptsFilter : undefined,
          page,
          limit: PAGE_SIZE,
        });
        setLeads(data.leads);
        setTotalCount(data.pagination.total);
        setTotalPages(data.pagination.totalPages);
        setCurrentPage(data.pagination.page);
        if (data.tabCounts) setTabCounts(data.tabCounts);
        setSelectionMeta(data.selectionMetadata || null);
      } catch {
        toast.error('Failed to load leads');
      } finally {
        setLeadsLoading(false);
      }
    },
    [selectedCampaignId, statusFilter, searchQuery, timezoneFilter, callAttemptsFilter]
  );

  const refreshLeadsSilent = useCallback(async () => {
    if (!selectedCampaignId) return;
    try {
      const data = await agentService.getMyResults({
        campaignId: selectedCampaignId,
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: searchQuery || undefined,
        timezone: timezoneFilter !== 'auto' ? timezoneFilter : undefined,
        callAttempts: callAttemptsFilter !== 'all' ? callAttemptsFilter : undefined,
        page: currentPage,
        limit: PAGE_SIZE,
      });
      setLeads(data.leads);
      setTotalCount(data.pagination.total);
      setTotalPages(data.pagination.totalPages);
      setCurrentPage(data.pagination.page);
      if (data.tabCounts) setTabCounts(data.tabCounts);
      setSelectionMeta(data.selectionMetadata || null);
    } catch {
      // Silent refresh only
    }
  }, [selectedCampaignId, statusFilter, searchQuery, currentPage, timezoneFilter, callAttemptsFilter]);

  useEffect(() => {
    setCurrentPage(1);
    fetchLeads(1);
  }, [fetchLeads]);

  // Refresh lead list when agent saves a disposition from the in-call popup
  useEffect(() => {
    setOnDispositionComplete((payload) => {
      if (payload?.leadId) {
        setMyDialingLeadIds((prev) => {
          const next = new Set(prev);
          next.delete(payload.leadId!);
          return next;
        });
        if (payload.disposition) {
          const nextStatus = dispositionToLeadStatus(payload.disposition);
          setLeads((prev) =>
            prev.map((lead) =>
              lead.id === payload.leadId ? { ...lead, status: nextStatus } : lead
            )
          );
        }
      }
      // Silent refresh: no spinner remount, so the agent keeps their scroll
      // position in the list after dispositioning a call.
      void refreshLeadsSilent();
    });
    return () => setOnDispositionComplete(null);
  }, [refreshLeadsSilent, setOnDispositionComplete]);

  // Realtime results invalidation via SSE with slow polling fallback
  useEffect(() => {
    if (!selectedCampaignId) return;

    let es: EventSource | null = null;
    let fallbackPoll: ReturnType<typeof setInterval> | null = null;
    let stopped = false;

    const startFallback = () => {
      if (fallbackPoll || stopped) return;
      // Keep a low-frequency fallback to avoid request storms if SSE is unavailable.
      fallbackPoll = setInterval(() => {
        void refreshLeadsSilent();
      }, 30000);
    };

    try {
      es = new EventSource(agentService.getMyResultsStreamUrl(selectedCampaignId));
      es.addEventListener('results-invalidated', () => {
        void refreshLeadsSilent();
      });
      es.onerror = () => {
        es?.close();
        es = null;
        startFallback();
      };
    } catch {
      startFallback();
    }

    return () => {
      stopped = true;
      if (es) es.close();
      if (fallbackPoll) clearInterval(fallbackPoll);
    };
  }, [selectedCampaignId, refreshLeadsSilent]);

  // Debounced search
  const handleSearchChange = (value: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 400);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    fetchLeads(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDialLead = async (lead: Lead) => {
    if (!selectedCampaignId) return;
    if (webrtcStatus !== 'ready' && webrtcStatus !== 'in-call') {
      toast.error('WebRTC not connected. Please wait for connection or refresh the page.');
      return;
    }

    // Immediately mark as dialing in UI
    setMyDialingLeadIds((prev) => new Set(prev).add(lead.id));
    try {
      // Step 1: Prepare the call (validate + create DB records + store conference config)
      const data = await agentService.prepareDial(lead.id, selectedCampaignId);

      // Step 2: Initiate a WebRTC call to the Telnyx connection (NOT the merchant).
      // The backend webhook will create a conference with this call and dial the
      // merchant as a separate participant. This enables supervisor listen/whisper/barge.
      makeOutboundCall({
        destinationNumber: data.fromNumber, // Call own number — backend intercepts, answers, dials merchant, bridges
        callerNumber: data.fromNumber,
        callAttemptId: data.callAttemptId,
        routingAttemptId: data.routingAttemptId,
        routingLegId: data.routingLegId,
        campaignId: data.campaignId,
        leadId: data.leadId,
        leadName: data.leadName,
        leadPhone: data.leadPhone || data.toNumber,
        leadEmail: data.leadEmail || null,
        companyName: data.companyName || null,
        campaignName: data.campaignName || null,
        scriptName: data.scriptName || null,
        scriptContent: data.scriptContent || null,
        scriptStructure: data.scriptStructure || null,
        agentId: data.agentId,
        leadStatus: data.leadStatus || null,
        preDialStatus: data.preDialStatus || null,
        subStageId: data.subStageId || null,
        dispositionMode: 'main_dial',
      });

      toast.success(`Dialing ${lead.full_name || lead.phone_e164}...`);
    } catch (error: any) {
      const errData = error?.response?.data?.error;
      const msg = typeof errData === 'string' ? errData : errData?.message || error?.message || 'Failed to dial';
      toast.error(msg);
      setMyDialingLeadIds((prev) => {
        const next = new Set(prev);
        next.delete(lead.id);
        return next;
      });
    }
  };

  const handleClearStuckCall = async () => {
    setClearingStuck(true);
    try {
      const result = await agentService.clearStuckCall();
      try {
        await hangUp();
      } catch {
        // WebRTC may already be idle
      }
      setMyDialingLeadIds(new Set());
      const parts: string[] = [];
      if (result.releasedAgentLock) parts.push('lock released');
      if (result.clearedCallAttempts > 0) parts.push(`${result.clearedCallAttempts} call(s)`);
      if (result.clearedRoutingAttempts > 0) parts.push(`${result.clearedRoutingAttempts} routing session(s)`);
      if (result.resetLeads > 0) parts.push(`${result.resetLeads} lead(s) reset`);
      toast.success(
        parts.length > 0
          ? `Cleared stuck state (${parts.join(', ')})`
          : 'No stuck state found — you can dial again'
      );
      fetchLeads(currentPage);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } | string } }; message?: string };
      const payload = err?.response?.data?.error;
      const msg =
        typeof payload === 'string'
          ? payload
          : payload?.message || err?.message || 'Failed to clear stuck call';
      toast.error(msg);
    } finally {
      setClearingStuck(false);
    }
  };

  const handleCancelDial = async (leadId: string, disposition?: string) => {
    setCancellingLeadId(leadId);
    try {
      await agentService.cancelDial({ leadId, ...(disposition ? { disposition } : {}) });
      toast.success('Call cancelled');
      setMyDialingLeadIds((prev) => {
        const next = new Set(prev);
        next.delete(leadId);
        return next;
      });
    } catch (error: any) {
      // Even on error, clear the dialing state so the agent can retry
      const errData2 = error?.response?.data?.error;
      const msg = typeof errData2 === 'string' ? errData2 : errData2?.message || error?.message || 'Failed to cancel';
      toast.error(msg);
      setMyDialingLeadIds((prev) => {
        const next = new Set(prev);
        next.delete(leadId);
        return next;
      });
    } finally {
      setCancellingLeadId(null);
      // Always refresh leads to get latest state
      fetchLeads(currentPage);
    }
  };

  const startEditNotes = (lead: Lead) => {
    setEditingNotesLeadId(lead.id);
    setNotesDraft(lead.notes || '');
  };

  const cancelEditNotes = () => {
    setEditingNotesLeadId(null);
    setNotesDraft('');
  };

  const saveLeadNotes = async (lead: Lead) => {
    const normalizedNotes = notesDraft.trim();
    setSavingNotesLeadId(lead.id);
    try {
      await leadService.updateLead(lead.id, { notes: normalizedNotes || '' });
      setLeads((prev) =>
        prev.map((item) =>
          item.id === lead.id ? { ...item, notes: normalizedNotes || null } : item
        )
      );
      toast.success('Lead notes updated');
      cancelEditNotes();
    } catch {
      toast.error('Failed to update lead notes');
    } finally {
      setSavingNotesLeadId(null);
    }
  };

  const isDialable = (lead: Lead) => !NON_DIALABLE_STATUSES.includes(lead.status);
  const isRedial = (lead: Lead) => !FIRST_DIAL_STATUSES.includes(lead.status) && isDialable(lead);
  const isBeingDialed = (lead: Lead) => myDialingLeadIds.has(lead.id);
  const isDialedByOther = (lead: Lead) => otherAgentLeads.has(lead.id) || (lead.status === 'calling' && !myDialingLeadIds.has(lead.id));

  // No manual campaigns available
  if (!campaignsLoading && manualCampaigns.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Manual Dialer</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manually dial leads from your assigned campaigns.
          </p>
        </div>
        <Card>
          <CardContent>
            <div className="text-center py-12">
              <PhoneArrowUpRightIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Manual Campaigns</h3>
              <p className="text-muted-foreground">
                You don't have any active manual dialing campaigns assigned to you.
                <br />
                Ask your admin to create a campaign with "Manual Dial" mode and assign you.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Manual Dialer</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Select a campaign and click Dial to call leads manually.
          </p>
        </div>
        <Button
          type="button"
          variant="outlined"
          onClick={handleClearStuckCall}
          disabled={clearingStuck}
          className="shrink-0 border-amber-500/50 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10"
        >
          {clearingStuck ? 'Clearing…' : 'Clear stuck call'}
        </Button>
      </div>

      {/* Campaign Selector + Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Campaign dropdown */}
        <div className="flex-1 max-w-xs">
          <label htmlFor="campaign-select" className="block text-sm font-medium text-muted-foreground mb-1">
            Campaign
          </label>
          <select
            id="campaign-select"
            value={selectedCampaignId}
            onChange={(e) => {
              setSelectedCampaignId(e.target.value);
              setStatusFilter('all');
              setCurrentPage(1);
            }}
            className="input w-full"
          >
            {campaignsLoading ? (
              <option>Loading...</option>
            ) : (
              manualCampaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.totalLeads} leads)
                </option>
              ))
            )}
          </select>
        </div>

        {/* Status filter */}
        <div className="flex-1 max-w-xs">
          <label htmlFor="status-filter" className="block text-sm font-medium text-muted-foreground mb-1">
            Status
          </label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="input w-full"
          >
            <option value="all">All{tabCounts['all'] !== undefined ? ` (${tabCounts['all']})` : ''}</option>
            {STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
                {tabCounts[option.value] !== undefined ? ` (${tabCounts[option.value]})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Timezone filter */}
        <div className="flex-1 max-w-xs">
          <label htmlFor="timezone-filter" className="block text-sm font-medium text-muted-foreground mb-1">
            Timezone
          </label>
          <select
            id="timezone-filter"
            value={timezoneFilter}
            onChange={(e) => { setTimezoneFilter(e.target.value); setCurrentPage(1); }}
            className="input w-full"
          >
            <option value="auto">
              Auto
              {selectionMeta?.source === 'campaign_timezone' && selectionMeta.effectiveTimezone
                ? ` (${selectionMeta.effectiveTimezone})`
                : ''}
            </option>
            <option value="Eastern">Eastern</option>
            <option value="Central">Central</option>
            <option value="Mountain">Mountain</option>
            <option value="Pacific">Pacific</option>
            <option value="Unknown">Unknown</option>
          </select>
        </div>

        {/* Call attempts filter */}
        <div className="flex-1 max-w-xs">
          <label htmlFor="attempts-filter" className="block text-sm font-medium text-muted-foreground mb-1">
            Call attempts
          </label>
          <select
            id="attempts-filter"
            value={callAttemptsFilter}
            onChange={(e) => { setCallAttemptsFilter(e.target.value); setCurrentPage(1); }}
            className="input w-full"
          >
            <option value="0">0 (not called yet)</option>
            <option value="1">1 attempt</option>
            <option value="2">2 attempts</option>
            <option value="3">3 attempts</option>
            <option value="4">4 attempts</option>
            <option value="5plus">5+ attempts</option>
            <option value="all">All</option>
          </select>
        </div>

        {/* Search */}
        <div className="flex-1 max-w-xs">
          <label htmlFor="search-input" className="block text-sm font-medium text-muted-foreground mb-1">
            Search
          </label>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              id="search-input"
              type="text"
              placeholder="Name, phone or company..."
              onChange={(e) => handleSearchChange(e.target.value)}
              className="input w-full pl-9"
            />
          </div>
        </div>
      </div>

      {/* Leads List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              Leads ({totalCount.toLocaleString()})
            </CardTitle>
            {totalPages > 1 && (
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {selectionMeta && (
            <div className="mb-3 p-3 rounded-md border border-border bg-muted/40">
              <p className="text-xs text-muted-foreground">
                {buildTimezoneFilterMessage(selectionMeta, timezoneFilter)}
                {' '}
                {selectionMeta.areaCodePriorityApplied
                  ? 'Leads are prioritized by area-code pickup rate.'
                  : 'Area-code priority is disabled for this campaign.'}
              </p>
            </div>
          )}
          {leadsLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No leads found.
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {leads.map((lead) => (
                  <div
                    key={lead.id}
                    className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-accent/30 transition-colors"
                  >
                    {/* Lead info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-foreground truncate">
                          {lead.full_name || 'Unknown'}
                        </h4>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                            lead.status === 'new'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                              : lead.status === 'interested'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : lead.status === 'not_interested'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                              : lead.status === 'follow_up'
                              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                              : 'bg-secondary text-secondary-foreground'
                          }`}
                        >
                          {lead.status.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
                        <p>
                          <span className="font-medium text-foreground/70">Phone:</span> {lead.phone_e164 || '—'}
                          {(lead.phone_number_count ?? 0) > 1 && (
                            <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" title={`${lead.active_phone_number_count ?? lead.phone_number_count} active of ${lead.phone_number_count} numbers`}>
                              {lead.phone_number_count} numbers
                            </span>
                          )}
                        </p>
                        <p><span className="font-medium text-foreground/70">Email:</span> {lead.email || '—'}</p>
                        <p><span className="font-medium text-foreground/70">Company:</span> {lead.company_name || '—'}</p>
                        <p>
                          <span className="font-medium text-foreground/70">Timezone:</span> {lead.timezone || 'Unknown'}
                          {' · '}
                          <span className="font-medium text-foreground/70">Area code:</span> {lead.area_code || '—'}
                          {' · '}
                          <span className="font-medium text-foreground/70">Priority:</span> {lead.priority_tier || 'Low'}
                        </p>
                      </div>
                      {lead.latest_call_status && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Last call: {lead.latest_call_status}
                          {lead.latest_reason && ` — ${lead.latest_reason}`}
                        </p>
                      )}
                      {editingNotesLeadId === lead.id ? (
                        <div className="mt-2 max-w-xl space-y-2">
                          <textarea
                            value={notesDraft}
                            onChange={(e) => setNotesDraft(e.target.value)}
                            rows={3}
                            maxLength={5000}
                            className="input w-full"
                            placeholder="Add notes about this lead..."
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => saveLeadNotes(lead)}
                              disabled={savingNotesLeadId === lead.id}
                              className="flex items-center gap-1.5"
                            >
                              <CheckIcon className="h-4 w-4" />
                              {savingNotesLeadId === lead.id ? 'Saving...' : 'Save Notes'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outlined"
                              onClick={cancelEditNotes}
                              disabled={savingNotesLeadId === lead.id}
                              className="flex items-center gap-1.5"
                            >
                              <XMarkIcon className="h-4 w-4" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-1 flex items-start gap-2">
                          <p className="text-xs text-muted-foreground italic truncate max-w-md" title={lead.notes || ''}>
                            Notes: {lead.notes || '—'}
                          </p>
                          <button
                            type="button"
                            onClick={() => startEditNotes(lead)}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            <PencilSquareIcon className="h-3.5 w-3.5" />
                            Edit notes
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Dial button */}
                    <div className="ml-4 flex-shrink-0 flex items-center gap-2">
                      {isBeingDialed(lead) ? (
                        <>
                          <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 animate-pulse font-medium">
                            <span className="h-2 w-2 rounded-full bg-yellow-500 animate-bounce" />
                            Dialing...
                          </span>
                          <button
                            onClick={() => handleCancelDial(lead.id, 'no_answer')}
                            disabled={cancellingLeadId === lead.id}
                            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 font-medium transition-colors disabled:opacity-50"
                            title="Cancel this call"
                          >
                            <PhoneXMarkIcon className="h-3.5 w-3.5" />
                            {cancellingLeadId === lead.id ? 'Ending...' : 'End Call'}
                          </button>
                        </>
                      ) : isDialedByOther(lead) ? (
                        <>
                          <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 font-medium">
                            <span className="h-2 w-2 rounded-full bg-orange-500 animate-pulse" />
                            {otherAgentLeads.get(lead.id) || 'Another agent'} is on this call
                          </span>
                          {lead.status === 'calling' && (
                            <button
                              onClick={() => handleCancelDial(lead.id, 'no_answer')}
                              disabled={cancellingLeadId === lead.id}
                              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 font-medium transition-colors disabled:opacity-50"
                              title="Force end stuck dialing attempt and unlock lead"
                            >
                              <PhoneXMarkIcon className="h-3.5 w-3.5" />
                              Force End
                            </button>
                          )}
                        </>
                      ) : isDialable(lead) ? (
                        <div className="flex items-center gap-2">
                          {lead.dial_count > 0 && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground font-medium" title={`Dialed ${lead.dial_count} time${lead.dial_count > 1 ? 's' : ''}`}>
                              {lead.dial_count}x
                            </span>
                          )}
                          <Button
                            size="sm"
                            onClick={() => handleDialLead(lead)}
                            disabled={myDialingLeadIds.size > 0 || (webrtcStatus !== 'ready' && webrtcStatus !== 'in-call')}
                            className="flex items-center gap-1.5"
                          >
                            <PhoneArrowUpRightIcon className="h-4 w-4" />
                            {isRedial(lead) ? 'Re-dial' : 'Dial'}
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {lead.dial_count > 0 && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground font-medium" title={`Dialed ${lead.dial_count} time${lead.dial_count > 1 ? 's' : ''}`}>
                              {lead.dial_count}x
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground italic">
                            Not dialable
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1}–
                    {Math.min(currentPage * PAGE_SIZE, totalCount)} of{' '}
                    {totalCount.toLocaleString()}
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outlined"
                      size="sm"
                      disabled={currentPage <= 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      <ChevronLeftIcon className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    {(() => {
                      const pages: number[] = [];
                      const maxVisible = 5;
                      let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                      let end = Math.min(totalPages, start + maxVisible - 1);
                      if (end - start + 1 < maxVisible) {
                        start = Math.max(1, end - maxVisible + 1);
                      }
                      for (let i = start; i <= end; i++) pages.push(i);
                      return pages.map((p) => (
                        <button
                          key={p}
                          onClick={() => handlePageChange(p)}
                          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                            p === currentPage
                              ? 'bg-primary text-primary-foreground font-medium'
                              : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                          }`}
                        >
                          {p}
                        </button>
                      ));
                    })()}
                    <Button
                      variant="outlined"
                      size="sm"
                      disabled={currentPage >= totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Next
                      <ChevronRightIcon className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentManualDialPage;
